import {AppPaths} from "@/app/AppPaths";

/**
 * Whether `target` is a safe in-app navigation target: exactly one leading slash (never `//`, which
 * some browsers treat as protocol-relative), and no backslash (which some browsers normalize to a
 * slash, turning a single leading slash into a protocol-relative `//`). A target that fails this check
 * could otherwise carry a user off BoxHub — e.g. via a crafted `redirect` query parameter.
 */
export function isSafeRedirectTarget(target: string): boolean {
    return target.startsWith("/") && !target.startsWith("//") && !target.includes("\\");
}

/**
 * Determines the sign-in target to navigate to when an unauthenticated user hits a protected route.
 *
 * A regular protected-route hit carries the original location back as a `redirect` query
 * parameter so the user lands where they started after signing in. An active logout never
 * carries a `redirect` parameter — the user explicitly chose to leave, so there is nothing to
 * return to.
 */
export function resolveSignInRedirectTarget(pathname: string, search: string, isLoggingOut: boolean): string {
    if (isLoggingOut) {
        return AppPaths.SIGN_IN;
    }
    const target = pathname + search;
    const safeTarget = isSafeRedirectTarget(target) ? target : AppPaths.LANDING_PAGE;
    const from = encodeURIComponent(safeTarget);
    return `${AppPaths.SIGN_IN}?redirect=${from}`;
}

/**
 * Resolves the `redirect` query parameter read back after sign-in into an actual navigation target.
 * Falls back to `fallback` when the parameter is absent or fails the {@link isSafeRedirectTarget} check —
 * the same normalization {@link resolveSignInRedirectTarget} applies when building the parameter, so the
 * protection does not depend on how the parameter reached the browser.
 */
export function resolveRedirectDestination(redirectParam: string | null, fallback: string): string {
    if (redirectParam && isSafeRedirectTarget(redirectParam)) {
        return redirectParam;
    }
    return fallback;
}
